'use client'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { dispatch, useSelector } from '@/lib/store'
import { updateCartData } from '@/lib/reducers/cartReducer'
import { updateLoginData } from '@/lib/reducers/loginReducer'
import { askUserToSelectPatient } from '@/lib/reducers/patientReducer'
import CART_HELPER, { isProductInCart } from '@/helper/cartHelper'
import useCustomToast from '@/hooks/useCustomToast'
import {
  DELETE_ACTION,
  INSERT_ACTION,
  TEST_INCLUDED_SECTION_ID,
} from '@/utils/constants'
import { getProductSectionUrl } from '@/utils/commonFunc'
import { useState } from 'react'
import PACKAGE_HELPER from '@/helper/packageHelper'
import { initSavingDto, updateOrderData } from '@/lib/reducers/orderReducer'
import Link from 'next/link'
import Image from 'next/image'
import ProductParametersModal from '../LandingPage/ProductParametersModal'
import CartActionButton from './CartActionButton'

interface TestCardProps {
  data: any
  variant?: 'default' | 'lead'
  imageUrl?: string
}

const TestCard: React.FC<TestCardProps> = ({
  data,
  variant = 'default',
  imageUrl,
}) => {
  const PRODUCT_DETAIL = data?.productDto

  const showToast = useCustomToast()

  const {
    handleAddOrRemoveProductInCart,
    handleGetCartItems,
    handleRestoreCartItems,
  } = CART_HELPER()
  const { handleCheckValidationForProduct, getTestOrPackageUrl } =
    PACKAGE_HELPER()

  const cartState: any = useSelector((state) => state.cart)
  const loginState = useSelector((state) => state.login)
  const userState = useSelector((state) => state.user)
  const patientState = useSelector((state) => state.patient)
  const orderState = useSelector((state) => state.order)
  const cityState = useSelector((state) => state.city)
  const [isParametersModalOpen, setIsParametersModalOpen] = useState(false)

  const [loading, setLoading] = useState(false)

  const isInCart = isProductInCart(cartState?.cartDtoList, PRODUCT_DETAIL?.id)

  const handleAddProduct = async () => {
    if (!loginState?.isUserLoggedIn) {
      dispatch(updateLoginData({ ...loginState, loginModalIsOpen: true }))
      return
    }
    setLoading(true)
    if (
      Array.isArray(userState?.membersList) &&
      userState?.membersList?.length > 1 &&
      (!cartState?.activePatientDetailDto ||
        Object.keys(cartState.activePatientDetailDto).length === 0)
    ) {
      dispatch(
        askUserToSelectPatient({
          ...patientState,
          selectPatientModalIsOpen: true,
          selectedTest: { ...data },
        })
      )
    } else {
      const validationResponse = await handleCheckValidationForProduct(data)
      if (!!validationResponse?.errorMessage) {
        showToast('danger', validationResponse.errorMessage)
        setLoading(false)
        return
      }
      const apiResponse = await handleAddOrRemoveProductInCart(
        INSERT_ACTION,
        userState,
        data
      )

      if (apiResponse?.status === true) {
        if (!!apiResponse?.data?.[0]?.sameProductAdded) {
          showToast('warning', apiResponse?.data?.[0]?.message)
        }
        if (!!apiResponse?.data?.[0]?.cityChanged) {
          showToast('warning', apiResponse?.data?.[0]?.message)
        }
        const res = await handleGetCartItems()

        dispatch(updateCartData({ ...cartState, ...(res?.data || {}) }))
        dispatch(
          updateOrderData({
            ...orderState,
            cartDtoList: [...(res?.data?.cartDtoList || [])],
            savingDto: initSavingDto,
          })
        )
      }
    }
    setLoading(false)
  }

  const handleRemoveProduct = async (data: any) => {
    if (!loginState?.isUserLoggedIn) {
      dispatch(updateLoginData({ ...loginState, loginModalIsOpen: true }))
      return
    }
    setLoading(true)
    try {
      const apiResponse = await handleAddOrRemoveProductInCart(
        DELETE_ACTION,
        userState,
        data
      )
      if (apiResponse?.status === true) {
        if (!!apiResponse?.data?.[0]?.cityChanged) {
          showToast('warning', apiResponse?.data?.[0]?.message)
        }
        const restoreApiResponse = await handleRestoreCartItems(
          userState?.phoneNumber,
          cityState?.currentCityObj?.uuid
        )
        if (restoreApiResponse?.status === true) {
          if (restoreApiResponse?.data?.capturedChange) {
            showToast(
              'warning',
              'Some test might get removed as the selected city address is changed'
            )
          }
          dispatch(
            updateCartData({
              ...cartState,
              ...(restoreApiResponse?.data || {}),
            })
          )
          dispatch(
            updateOrderData({
              ...orderState,
              cartDtoList: restoreApiResponse?.data?.cartDtoList || [],
              savingDto: initSavingDto,
            })
          )
        }
        const res = await handleGetCartItems()
        dispatch(updateCartData({ ...cartState, ...(res?.data || {}) }))
        dispatch(
          updateOrderData({
            ...orderState,
            cartDtoList: [...(res?.data?.cartDtoList || [])],
          })
        )
      }
    } finally {
      // A failed DELETE used to leave the button stuck on "Removing".
      setLoading(false)
    }
  }

  const openParametersModal = () => {
    setIsParametersModalOpen(true)
  }

  // Modified clicks keep their native behaviour so cmd/ctrl-click still opens
  // the detail page in a new tab. A plain click opens the modal, exactly as the
  // old button did. Middle-click fires auxclick, never onClick, so it already
  // bypasses this.
  const handleParametersLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    openParametersModal()
  }

  // On the lead page both card links point at #get-callback. Next skips its hash
  // scroll when the hash is already set, so scroll explicitly to keep repeat
  // clicks working. Idempotent — same target when Next does scroll.
  const handleScrollToCallbackForm = () => {
    document.getElementById('get-callback')?.scrollIntoView()
  }

  const productUrl = getTestOrPackageUrl(PRODUCT_DETAIL)

  const parametersHref = getProductSectionUrl(
    productUrl,
    TEST_INCLUDED_SECTION_ID
  )
  const parametersLabel = `View parameters for ${
    PRODUCT_DETAIL?.name || 'this test'
  }`
  // Shared so the link and button branches cannot drift apart visually.
  const parametersTriggerClass =
    'relative z-10 text-body-sm-medium text-primary-500 hover:text-primary-600 cursor-pointer bg-transparent border-none p-0 min-w-0 truncate text-right'

  return (
    <Card
      tabIndex={0}
      className={`group bg-white relative flex flex-col rounded-xl transition-all duration-300 w-full cursor-pointer ${
        imageUrl
          ? 'h-auto border-0 shadow-elevation5'
          : 'h-[272px] border border-gray-300 shadow-none'
      }`}
      role="article"
      aria-label={
        PRODUCT_DETAIL?.name
          ? `Test card for ${PRODUCT_DETAIL.name}`
          : 'Test card'
      }
    >
      {/* Full-card clickable overlay link */}
      <Link
        href={variant === 'lead' ? '#get-callback' : productUrl}
        onClick={variant === 'lead' ? handleScrollToCallbackForm : undefined}
        className="absolute inset-0 z-0 rounded-xl"
        aria-hidden="true"
        tabIndex={-1}
      />

      {PRODUCT_DETAIL?.mostBooked && (
        <div className="absolute right-0 top-4 z-10">
          <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white text-label-xs font-semibold py-1.5 px-3 rounded-l-full">
            Most Booked
          </div>
        </div>
      )}

      {imageUrl && (
        <div className="h-40 w-full rounded-t-xl overflow-hidden flex-shrink-0">
          <Image
            src={imageUrl}
            alt={PRODUCT_DETAIL?.name || 'Package image'}
            width={320}
            height={160}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="px-5 pt-5 pb-[76px] flex-1">
        <div className="flex flex-col gap-4">
          {/* Title & Report TAT */}
          <CardHeader className="p-0 space-y-2 shrink-0">
            {!!PRODUCT_DETAIL?.name && (
              <CardTitle className="text-heading-xs text-[#1f1f1f] line-clamp-2">
                {PRODUCT_DETAIL?.name}
              </CardTitle>
            )}
            <div className="border-b border-dashed border-gray-200 w-full" />
            <div className="flex items-center justify-between gap-2 w-full">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.3335 7.99967C13.3335 10.9448 10.9458 13.333 8.00016 13.333C5.05448 13.333 2.66683 10.9448 2.66683 7.99967C2.66683 5.05389 5.05438 2.66634 8.00016 2.66634C10.9459 2.66634 13.3335 5.05389 13.3335 7.99967ZM8.00016 14.6663C11.6823 14.6663 14.6668 11.6811 14.6668 7.99967C14.6668 4.31751 11.6823 1.33301 8.00016 1.33301C4.318 1.33301 1.3335 4.31751 1.3335 7.99967C1.3335 11.6811 4.318 14.6663 8.00016 14.6663ZM8.42375 5.46354C8.42375 5.09535 8.12527 4.79688 7.75708 4.79688C7.38889 4.79688 7.09041 5.09535 7.09041 5.46354V8.49919C7.09041 8.73379 7.21371 8.95111 7.41509 9.07146L9.76679 10.4769C10.0828 10.6657 10.4922 10.5626 10.681 10.2466C10.8699 9.93054 10.7668 9.52121 10.4508 9.33234L8.42375 8.12096V5.46354Z"
                    fill="#888582"
                  />
                </svg>
                <span className="text-body-sm text-gray-600">Reports</span>
              </div>
              {/* Type-checked, not just truthy: reportTat comes off an `any` payload
              and .toLowerCase() on a numeric value throws. */}
              {typeof PRODUCT_DETAIL?.reportTat === 'string' &&
                !!PRODUCT_DETAIL.reportTat.trim() && (
                  <span className="text-body-sm-medium text-gray-900 truncate min-w-0 text-right">
                    {PRODUCT_DETAIL.reportTat.toLowerCase().startsWith('within')
                      ? PRODUCT_DETAIL.reportTat
                      : `Within ${PRODUCT_DETAIL.reportTat}`}
                  </span>
                )}
            </div>
          </CardHeader>

          {/* Parameters Button */}
          {PRODUCT_DETAIL?.numberOfTests && (
            <div className="flex items-center justify-between gap-2 w-full">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.33333 4H14M5.33333 8H14M5.33333 12H14M2 4H2.00667M2 8H2.00667M2 12H2.00667"
                    stroke="#888582"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-body-sm text-gray-600">Parameters</span>
              </div>
              <Link
                href={parametersHref}
                // The full-card overlay Link already warms this route; a second
                // prefetch per card buys nothing on a 20-card listing.
                prefetch={false}
                onClick={handleParametersLinkClick}
                aria-label={parametersLabel}
                aria-haspopup="dialog"
                className={parametersTriggerClass}
              >
                {PRODUCT_DETAIL?.numberOfTests}
              </Link>
            </div>
          )}

          <div className="border-b border-dashed border-gray-200 w-full" />

          {/* Pricing Row */}
          <div className="flex items-center gap-2 w-full">
            {!!PRODUCT_DETAIL?.price && (
              <span className="text-heading-sm font-semibold text-[#1f1f1f]">
                &#8377;{PRODUCT_DETAIL?.price}
              </span>
            )}
            {!!PRODUCT_DETAIL?.marketPrice &&
              PRODUCT_DETAIL?.price !== PRODUCT_DETAIL?.marketPrice && (
                <span className="text-body-sm font-medium text-[#a09b98] line-through">
                  &#8377;{PRODUCT_DETAIL?.marketPrice}
                </span>
              )}
            {!!PRODUCT_DETAIL?.discountPercentage && (
              <span className="text-label-sm-medium text-[#119759] bg-[#d9fce5] px-2 py-0.5 rounded whitespace-nowrap">
                Save {PRODUCT_DETAIL?.discountPercentage}%
              </span>
            )}
          </div>
        </div>

        {/* Action Button — absolutely anchored to bottom of card */}
        <div className="absolute bottom-5 left-5 right-5 z-10">
          {variant === 'lead' ? (
            <Link
              href="#get-callback"
              onClick={handleScrollToCallbackForm}
              className="relative z-10 block w-full text-center text-label-md-medium text-white-100 bg-primary-600 hover:bg-primary-700 rounded-lg py-2.5 shadow-none transition-all duration-200 active:scale-[0.97]"
            >
              Book Now
            </Link>
          ) : (
            // relative z-10 keeps the button above the full-card overlay link.
            <CartActionButton
              isInCart={isInCart}
              loading={loading}
              onAdd={handleAddProduct}
              onRemove={() => handleRemoveProduct(PRODUCT_DETAIL)}
              productName={PRODUCT_DETAIL?.name}
              className="relative z-10"
            />
          )}
        </div>
      </div>

      <ProductParametersModal
        productId={PRODUCT_DETAIL?.id}
        open={isParametersModalOpen}
        onOpenChange={setIsParametersModalOpen}
      />
    </Card>
  )
}

export default TestCard
